import {
  ApartmentOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Avatar, Button, Dropdown, Space, Tag, Tooltip, Typography } from 'antd';
import { useAuth } from '@/auth/OlAuthContext';
import { getHub, realmForHub } from '@/auth/hubStore';
import { getTenantIdentity } from '@/api/tenantAdmin';
import type { TenantIdentityView } from '@/api/types';
import { HelpMenu } from '@/edu/HelpMenu';
import { openPalette } from '@/palette/paletteStore';
import { ThemeToggle } from './ThemeToggle';

const { Text } = Typography;

/** macOS renders the Command glyph; everything else says Ctrl. Guessing wrong teaches a wrong chord. */
const IS_MAC =
  typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.userAgent ?? '');
const PALETTE_CHORD = IS_MAC ? '⌘K' : 'Ctrl K';

/**
 * The palette's visible affordance. A keyboard-first feature that is only documented is a feature
 * most operators never find: the chord is printed on the trigger, so the mouse path teaches the
 * keyboard path (WCAG 2.2 — shortcuts discoverable on screen, not only in help).
 */
function PaletteTrigger() {
  return (
    <Tooltip title={`Search and jump anywhere (${PALETTE_CHORD})`}>
      <Button
        type="text"
        onClick={openPalette}
        aria-label={`Open the command palette (${PALETTE_CHORD})`}
        aria-keyshortcuts="Meta+K Control+K"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <SearchOutlined />
        <span className="ol-kbd" aria-hidden>
          {PALETTE_CHORD}
        </span>
      </Button>
    </Tooltip>
  );
}

const HUB_LABEL = import.meta.env.VITE_HUB_LABEL || 'KYC Hub';

/**
 * Tenant identity chip (OPSFL-IAMUI-1 → OPSFL-THIAM-1): the hub the session actually belongs to —
 * SERVER-OWNED identity. The slug comes from the sign-in realm (realm-per-hub; the JWT's realm is
 * what every API call is isolated by), so the chip can never disagree with what the server
 * enforces. The DISPLAY NAME now comes from `GET /v1/tenants/self/identity` (control-plane
 * registry, tab-cached); until it loads — or on an older server without the endpoint — the chip
 * falls back to the build label + slug, never a guess. There is deliberately NO tenant switcher:
 * to work in another hub you sign in to that hub's realm. Mock mode (VITE_AUTH_DISABLED) is
 * labelled as such — never dressed up as a tenant.
 */
function TenantIdentityChip({ mock }: { mock: boolean }) {
  const hub = getHub();
  const [identity, setIdentity] = useState<TenantIdentityView | null>(null);

  useEffect(() => {
    if (mock || !hub) return undefined;
    let alive = true;
    void getTenantIdentity().then((view) => {
      if (alive) setIdentity(view);
    });
    return () => {
      alive = false;
    };
  }, [mock, hub]);

  if (mock) {
    return (
      <Tooltip title="Dev-standalone mock session — no realm, no server-owned tenant identity.">
        <Tag icon={<ApartmentOutlined />} bordered={false} color="warning">
          mock session · no hub
        </Tag>
      </Tooltip>
    );
  }
  if (!hub) return null; // pre-sign-in chrome — identity is unknown, show nothing rather than a guess

  // The server's display name when it differs from the slug; the slug always stays visible
  // (it is the isolation key an operator quotes in a support ticket).
  const serverName = identity && identity.displayName !== identity.hubId ? identity.displayName : null;
  return (
    <Tooltip
      title={
        <span>
          Hub <b>{hub}</b> — realm <span className="ol-mono">{realmForHub(hub)}</span>.
          {identity && (
            <>
              {' '}
              Variant: {identity.storageTier} / {identity.deploymentMode}.
            </>
          )}{' '}
          Identity is server-owned (set by your sign-in realm); there is no tenant switcher.
        </span>
      }
    >
      <Tag
        icon={<ApartmentOutlined />}
        bordered={false}
        tabIndex={0}
        aria-label={`Signed in to hub ${serverName ?? hub} (realm ${realmForHub(hub)})`}
        style={{ background: 'var(--ol-bg-subtle)', color: 'var(--ol-text-secondary)' }}
      >
        {serverName ?? HUB_LABEL} · <span className="ol-mono">{hub}</span>
      </Tag>
    </Tooltip>
  );
}

export function TopBar({
  collapsed,
  onToggleCollapse,
  title,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
  title?: React.ReactNode;
}) {
  const auth = useAuth();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        height: '100%',
        paddingInline: 12,
      }}
    >
      <Button
        type="text"
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={onToggleCollapse}
      />

      <div style={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
        {title ?? <Text strong>OneLattice</Text>}
      </div>

      <PaletteTrigger />

      <TenantIdentityChip mock={auth.mock} />

      <HelpMenu />

      <ThemeToggle />

      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'id',
              label: (
                <div style={{ padding: '4px 2px', lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 600 }}>{auth.user?.name ?? 'User'}</div>
                  {auth.user?.email && (
                    <div style={{ fontSize: 12, color: 'var(--ol-text-tertiary)' }}>
                      {auth.user.email}
                    </div>
                  )}
                  {auth.user?.roles?.length ? (
                    <div style={{ marginTop: 4 }}>
                      {auth.user.roles.slice(0, 4).map((r) => (
                        <Tag key={r} bordered={false} style={{ fontSize: 10, marginBottom: 2 }}>
                          {r}
                        </Tag>
                      ))}
                    </div>
                  ) : null}
                </div>
              ),
              disabled: true,
            },
            { type: 'divider' },
            {
              key: 'logout',
              icon: <LogoutOutlined />,
              label: auth.mock ? 'Sign out (disabled — mock session)' : 'Sign out',
              disabled: auth.mock,
              onClick: () => auth.logout(),
            },
          ],
        }}
      >
        <Button type="text" style={{ height: 40, paddingInline: 6 }}>
          <Space size={8}>
            <Avatar size={26} icon={<UserOutlined />} style={{ background: 'var(--ol-primary)' }} />
            <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {auth.user?.name ?? 'User'}
            </span>
            {auth.mock && (
              <Tag color="warning" bordered={false} style={{ fontSize: 10 }}>
                auth off
              </Tag>
            )}
          </Space>
        </Button>
      </Dropdown>
    </div>
  );
}
